/**
 * Maps exact route names to the feature(s) required for a subscription to appear
 * in the account selector when navigating to that route.
 * Multiple features are treated as OR — a subscription matching any one qualifies.
 * Routes not in this map fall back to showing all subscriptions.
 *
 * Every route that `getFeaturesForRoute` should handle must be explicitly listed here.
 * Do NOT use prefix/partial matching — it makes refactoring routes dangerous.
 */
const ROUTE_FEATURE_MAP: Record<string, string[]> = {
  // Rates
  'hotel.rates': ['app.rates', 'app.pricing_assistant'],
  'hotel.rates.index': ['app.rates', 'app.pricing_assistant'],
  'hotel.rates.detail': ['app.rates', 'app.pricing_assistant'],
  'hotel.rates.export': ['app.rates', 'app.pricing_assistant'],

  // Pricing Manager
  'hotel.pricing-manager': ['app.rates', 'app.pricing_assistant'],
  'hotel.pricing-manager.index': ['app.rates', 'app.pricing_assistant'],
  'hotel.pricing-manager.bulk-update': ['app.rates', 'app.pricing_assistant'],
  'hotel.pricing-manager.detail': ['app.rates', 'app.pricing_assistant'],

  // Strategy
  'hotel.strategy': ['app.rate_strategy'],
  'hotel.strategy.index': ['app.rate_strategy'],
  'hotel.strategy.cancellation': ['app.rate_strategy'],
  'hotel.strategy.dow': ['app.rate_strategy'],
  'hotel.strategy.leadtime': ['app.rate_strategy'],
  'hotel.strategy.los': ['app.rate_strategy'],
  'hotel.strategy.mealtype': ['app.rate_strategy'],
  'hotel.strategy.membership': ['app.rate_strategy'],
  'hotel.strategy.mobile': ['app.rate_strategy'],
  'hotel.strategy.pos': ['app.rate_strategy'],
  'hotel.strategy.position': ['app.rate_strategy'],
  'hotel.strategy.roomtype': ['app.rate_strategy'],

  // Day by Day
  'hotel.day-by-day': ['spider.day_by_day'],
  'hotel.day-by-day.index': ['spider.day_by_day'],
  'hotel.day-by-day.spider-dashboard': ['spider.day_by_day'],

  // Snapshot
  'hotel.snapshot': ['spider.hotel_snapshot'],
  'hotel.snapshot.index': ['spider.hotel_snapshot'],
  'hotel.snapshot.spider-dashboard': ['spider.hotel_snapshot'],

  // RevPAK
  'hotel.revpak': ['spider.revpak'],
  'hotel.revpak.index': ['spider.revpak'],
  'hotel.revpak.spider-dashboard': ['spider.revpak'],

  // Segment Drilldown
  'hotel.segment-drilldown': ['spider.market_segment_drilldown'],

  // Accounts
  'hotel.accounts': ['spider.account_analysis'],

  // RevPAK Exports
  'hotel.revpak-exports': ['spider.revpak_library'],

  // Channel Pricing
  'hotel.channel-pricing': ['app.smart_distribution'],

  // Channel Manager
  'hotel.channel-manager': ['app.channel_manager'],

  // Short Term Rental
  'hotel.short-term-rental': ['app.short_term_rental'],

  // Events
  'hotel.events': ['app.events'],
  'hotel.events.index': ['app.events'],
  'hotel.events.edit': ['app.events'],

  // Parity
  'hotel.parity': ['app.parity'],
  'hotel.parity.index': ['app.parity'],
  'hotel.parity.insight': ['app.parity'],
  'hotel.parity.insight.index': ['app.parity'],
  'hotel.parity.insight.trends': ['app.parity'],
  'hotel.parity.pos': ['app.parity'],
  'hotel.parity.list': ['app.parity'],
  'hotel.parity.overview': ['app.parity'],

  // Ranking
  'hotel.ranking': ['app.ranking'],

  // Benchmark Insight
  'hotel.benchmark-insight': ['app.benchmark_insight'],
  'hotel.benchmark-insight.index': ['app.benchmark_insight'],
  'hotel.benchmark-insight.desired-compset': ['app.benchmark_insight'],
  'hotel.benchmark-insight.market-composition': ['app.benchmark_insight'],
  'hotel.benchmark-insight.onboarding': ['app.benchmark_insight'],
  'hotel.benchmark-insight.terms-and-conditions': ['app.benchmark_insight'],
  'hotel.benchmark-insight.upsell': ['app.benchmark_insight'],

  // Revenue Insight
  'hotel.revenue-insight': ['app.revenue_insight'],
  'hotel.revenue-insight.index': ['app.revenue_insight'],
  'hotel.revenue-insight.breakdown-detail': ['app.revenue_insight'],
  'hotel.revenue-insight.detail': ['app.revenue_insight'],

  // Market Insight
  'hotel.market-insight': ['app.market_insight'],

  // Reservation Manager
  'hotel.reservation-manager': ['app.reservation_manager'],

  // Overview
  'hotel.overview': ['overview.view'],
  'hotel.overview.index': ['overview.view'],
  'hotel.overview.settings': ['overview.view'],

  // Forecast
  'hotel.forecast': ['app.forecast'],

  // Budget
  'hotel.budget': ['app.budget'],

  // Reviews
  'hotel.reviews': ['app.review_manager'],

  // Settings — mapped to their parent feature
  'hotel.settings.hotels': ['settings.hotel_access.edit'],
  'hotel.settings.reports': ['reports.view.settings'],
  'hotel.settings.spider': ['spider.settings.edit'],
  'hotel.settings.spider.spider-dashboard': ['spider.settings.edit'],
  'hotel.settings.myotb': ['otb.selfservice.view'],
  'hotel.settings.myotb.index': ['otb.selfservice.view'],
  'hotel.settings.myotb.active': ['otb.selfservice.view'],
  'hotel.settings.myotb.benchmark': ['otb.selfservice.view'],
  'hotel.settings.myotb.in-progress': ['otb.selfservice.view'],
  'hotel.settings.myotb.intro': ['otb.selfservice.view'],
  'hotel.settings.myotb.intro.index': ['otb.selfservice.view'],
  'hotel.settings.myotb.intro.contact-admin': ['otb.selfservice.view'],
  'hotel.settings.myotb.intro.contact-support': ['otb.selfservice.view'],
  'hotel.settings.myotb.other-system-eligibility': ['otb.selfservice.view'],
  'hotel.settings.myotb.other-system-selection': ['otb.selfservice.view'],
  'hotel.settings.myotb.pms-eligibility': ['otb.selfservice.view'],
  'hotel.settings.myotb.pms-selection': ['otb.selfservice.view'],
  'hotel.settings.myotb.pms-setup': ['otb.selfservice.view'],
  'hotel.settings.myotb.pms-unsupported': ['otb.selfservice.view'],
  'hotel.settings.myotb.validation': ['otb.selfservice.view'],
  'hotel.settings.central-mapping.menu': [
    'settings.mapping',
    'app.pricing_assistant',
    'app.rates',
    'app.parity',
    'benchmark_insight.pms.view',
  ],
  'hotel.settings.taxes': ['app.rates'],
  'hotel.settings.pos': ['app.rate_strategy'],
  'hotel.settings.recommendations': ['app.pricing_assistant'],
  'hotel.settings.recommendations.index': ['app.pricing_assistant'],
  'hotel.settings.recommendations.competitors-impact': [
    'app.pricing_assistant',
  ],
  'hotel.settings.recommendations.price-range': ['app.pricing_assistant'],
  'hotel.settings.autopilot': ['app.pricing_assistant'],
  'hotel.settings.pms-integration': ['app.pricing_assistant'],
  'hotel.settings.preferred-rate': ['app.pricing_assistant'],
  'hotel.settings.budget': [
    'business_intelligence.settings.budget_and_forecast',
  ],
  'hotel.settings.budget.index': [
    'business_intelligence.settings.budget_and_forecast',
  ],
  'hotel.settings.budget.detail': [
    'business_intelligence.settings.budget_and_forecast',
  ],
  'hotel.settings.forecast': [
    'business_intelligence.settings.budget_and_forecast',
  ],
  'hotel.settings.forecast.index': [
    'business_intelligence.settings.budget_and_forecast',
  ],
  'hotel.settings.forecast.detail': [
    'business_intelligence.settings.budget_and_forecast',
  ],
  'hotel.settings.competitors': [
    'settings.compset.app.view',
    'settings.compset.rms.view',
    'benchmark_insight.settings.compset.view',
    'benchmark_insight.settings.smart_compset.view',
    'benchmark_insight.settings.portfolio_compset.view',
  ],
  'hotel.settings.competitors.index': [
    'settings.compset.app.view',
    'settings.compset.rms.view',
    'benchmark_insight.settings.compset.view',
    'benchmark_insight.settings.smart_compset.view',
    'benchmark_insight.settings.portfolio_compset.view',
  ],
  'hotel.settings.competitors.general': [
    'settings.compset.app.view',
    'settings.compset.rms.view',
  ],
  'hotel.settings.competitors.benchmark': [
    'benchmark_insight.settings.compset.view',
    'benchmark_insight.settings.smart_compset.view',
    'benchmark_insight.settings.portfolio_compset.view',
  ],
  'hotel.settings.competitors.benchmark.index': [
    'benchmark_insight.settings.compset.view',
    'benchmark_insight.settings.smart_compset.view',
    'benchmark_insight.settings.portfolio_compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.index': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.builder': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.builder.index': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.builder.desired-compset': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.builder.generate': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.builder.proposals': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.compset.builder.review': [
    'benchmark_insight.settings.compset.view',
  ],
  'hotel.settings.competitors.benchmark.smart-compset': [
    'benchmark_insight.settings.smart_compset.view',
  ],
  'hotel.settings.competitors.benchmark.smart-compset.categories': [
    'benchmark_insight.settings.smart_compset.view',
  ],
  'hotel.settings.competitors.benchmark.smart-compset.review': [
    'benchmark_insight.settings.smart_compset.view',
  ],
  'hotel.settings.competitors.benchmark.smart-compset.validation': [
    'benchmark_insight.settings.smart_compset.view',
  ],
  'hotel.settings.competitors.benchmark.smart-compset.weights': [
    'benchmark_insight.settings.smart_compset.view',
  ],

  // Hotel — Settings: Currency Conversion
  'hotel.settings.currency-conversion.index': [
    'settings.exchange_rate_profiles.view',
  ],
  'hotel.settings.currency-conversion.profile': [
    'settings.exchange_rate_profiles.view',
  ],
};

export function getFeaturesForRoute(routeName: string): string[] | undefined {
  return ROUTE_FEATURE_MAP[routeName];
}

interface PreferenceGroup {
  candidates: { featureName: string; routeName: string }[];
  key: string;
}

const RATES_TAB_GROUP: PreferenceGroup = {
  key: 'rates-tab',
  candidates: [
    {
      featureName: 'app.pricing_assistant',
      routeName: 'hotel.pricing-manager',
    },
    {
      featureName: 'app.pricing_assistant',
      routeName: 'hotel.pricing-manager.index',
    },
    { featureName: 'app.rates', routeName: 'hotel.rates' },
    { featureName: 'app.rates', routeName: 'hotel.rates.index' },
  ],
};

/**
 * Maps route names to their UHV preference group. When the user visits any
 * route in a group, the preference is stored under the group key with the
 * landing route that matches the subscription's features.
 *
 * Routes NOT listed here use their own route name as the preference key
 * (the default behaviour).
 */
const ROUTE_TO_GROUP = new Map<string, PreferenceGroup>(
  RATES_TAB_GROUP.candidates.map(({ routeName }) => [
    routeName,
    RATES_TAB_GROUP,
  ]),
);

export function getPreferenceGroup(
  routeName: string,
): PreferenceGroup | undefined {
  return ROUTE_TO_GROUP.get(routeName);
}

/**
 * Maps exact route names to the translation key for the sidebar / tab label
 * shown on the account selection page.
 *
 * Every route that calls `redirectToAccountSelection` must be listed here.
 * Do NOT use prefix/partial matching — it makes refactoring routes dangerous.
 */
export const ROUTE_LABEL_MAP = {
  // Events
  'hotel.events': 'menu.events',
  'hotel.events.index': 'menu.events',
  'hotel.events.edit': 'menu.events',

  // Settings — Budget
  'hotel.settings.budget': 'settings.budget.title',
  'hotel.settings.budget.index': 'settings.budget.title',
  'hotel.settings.budget.detail': 'settings.budget.title',

  // Settings — Forecast
  'hotel.settings.forecast': 'settings.forecast.title',
  'hotel.settings.forecast.index': 'settings.forecast.title',
  'hotel.settings.forecast.detail': 'settings.forecast.title',

  // Settings — Competitors
  'hotel.settings.competitors': 'general.competitors',
  'hotel.settings.competitors.index': 'general.competitors',
  'hotel.settings.competitors.general': 'general.competitors',
  'hotel.settings.competitors.benchmark': 'general.competitors',
  'hotel.settings.competitors.benchmark.index': 'general.competitors',
  'hotel.settings.competitors.benchmark.compset': 'general.competitors',
  'hotel.settings.competitors.benchmark.compset.index': 'general.competitors',
  'hotel.settings.competitors.benchmark.compset.builder': 'general.competitors',
  'hotel.settings.competitors.benchmark.compset.builder.index':
    'general.competitors',
  'hotel.settings.competitors.benchmark.compset.builder.desired-compset':
    'general.competitors',
  'hotel.settings.competitors.benchmark.compset.builder.generate':
    'general.competitors',
  'hotel.settings.competitors.benchmark.compset.builder.proposals':
    'general.competitors',
  'hotel.settings.competitors.benchmark.compset.builder.review':
    'general.competitors',
  'hotel.settings.competitors.benchmark.smart-compset': 'general.competitors',
  'hotel.settings.competitors.benchmark.smart-compset.categories':
    'general.competitors',
  'hotel.settings.competitors.benchmark.smart-compset.review':
    'general.competitors',
  'hotel.settings.competitors.benchmark.smart-compset.validation':
    'general.competitors',
  'hotel.settings.competitors.benchmark.smart-compset.weights':
    'general.competitors',

  // Settings — Recommendations
  'hotel.settings.recommendations':
    'pricing_assistant.settings.navigation.label.recommendation',
  'hotel.settings.recommendations.index':
    'pricing_assistant.settings.navigation.label.recommendation',
  'hotel.settings.recommendations.competitors-impact':
    'pricing_assistant.settings.navigation.label.recommendation',
  'hotel.settings.recommendations.price-range':
    'pricing_assistant.settings.navigation.label.recommendation',

  // Settings — Spider / BI
  'hotel.settings.spider': 'settings.section.business_intelligence',
  'hotel.settings.spider.spider-dashboard':
    'settings.section.business_intelligence',

  // Settings — leaf routes
  'hotel.settings.activity': 'settings.section.activity_history',
  'hotel.settings.pos': 'general.pos',
  'hotel.settings.taxes': 'general.taxes',
  'hotel.settings.pms-integration':
    'pricing_assistant.settings.navigation.label.pms_integration',
  'hotel.settings.preferred-rate':
    'pricing_assistant.settings.navigation.label.preferred_rate',
  'hotel.settings.autopilot':
    'pricing_assistant.autopilot.settings.navigation.label',

  // Settings — My OTB
  'hotel.settings.myotb': 'settings.my_otb.setup',
  'hotel.settings.myotb.index': 'settings.my_otb.setup',

  // Settings — Central Mapping
  'hotel.settings.central-mapping.menu': 'settings.central-mapping.mapping',
};

export type RouteLabelKey = keyof typeof ROUTE_LABEL_MAP;
